import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { todayInTimezone } from '@/utils/dateUtils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/i18n/LanguageContext';

function formatDuration(seconds) {
  const s = Math.max(0, Number(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function CategoryBadge({ category }) {
  const c = (category || 'unlisted').toLowerCase();
  if (c === 'allowed') return <Badge className="bg-indigo-100 text-indigo-700">Allowed</Badge>;
  if (c === 'blocked') return <Badge className="bg-red-100 text-red-700">Blocked</Badge>;
  return <Badge className="bg-gray-100 text-gray-700">Unlisted</Badge>;
}

export default function UsageDashboardPage() {
  const { t, isRTL } = useLanguage();
  const [companyTimezone, setCompanyTimezone] = useState('UTC');
  const [selectedDate, setSelectedDate] = useState(todayInTimezone('UTC'));
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');
  const [staffId, setStaffId] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [policyMap, setPolicyMap] = useState({});

  useEffect(() => {
    const loadCompany = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings/company`, { withCredentials: true });
        const tz = res.data?.timezone || 'UTC';
        setCompanyTimezone(tz);
        setSelectedDate(todayInTimezone(tz));
      } catch (e) {
        // ignore
      }
    };
    loadCompany();
  }, []);

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const res = await axios.get(`${API}/admin/staff`, { withCredentials: true });
        setStaffList(res.data || []);
      } catch (e) {
        // ignore
      }
    };
    loadStaff();
  }, []);

  useEffect(() => {
    const loadPolicies = async () => {
      try {
        const res = await axios.get(`${API}/admin/usage-policies`, { withCredentials: true });
        const map = {};
        for (const p of res.data || []) {
          map[p.policy_id] = p;
        }
        setPolicyMap(map);
      } catch (e) {
        // ignore
      }
    };
    loadPolicies();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [summaryRes, itemsRes] = await Promise.all([
        axios.get(`${API}/admin/usage-dashboard/summary`, {
          params: { date: selectedDate },
          withCredentials: true
        }),
        axios.get(`${API}/admin/usage-dashboard/items`, {
          params: {
            date: selectedDate,
            q: q || undefined,
            staff_id: staffId === 'all' ? undefined : staffId,
            category: category === 'all' ? undefined : category,
            page,
            limit
          },
          withCredentials: true
        })
      ]);

      setSummary(summaryRes.data);
      setItems(itemsRes.data.items || []);
    } catch (e) {
      console.error('Failed to load usage dashboard:', e);
      setSummary(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [selectedDate, staffId, category, page]);

  const filters = useMemo(() => {
    return {
      date: selectedDate,
      staff_id: staffId,
      category,
      q
    };
  }, [selectedDate, staffId, category, q]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Dashboard</h1>
          <p className="text-gray-600">Allowed / Blocked / Unlisted usage overview</p>
        </div>
        <div className="text-sm text-gray-600">Timezone: {companyTimezone}</div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Search and filter usage by date, staff, and category.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="space-y-2">
            <Label>{t("common.date")}</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setPage(1);
              }}
              data-testid="usage-date"
            />
          </div>

          <div className="space-y-2">
            <Label>Staff</Label>
            <Select
              value={staffId}
              onValueChange={(v) => {
                setStaffId(v);
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All staff</SelectItem>
                {staffList.map((s) => (
                  <SelectItem key={s.staff_id} value={s.staff_id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <Select
              value={category}
              onValueChange={(v) => {
                setCategory(v);
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="allowed">Allowed</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="unlisted">Unlisted</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label>Search (app / domain / url)</Label>
            <div className="flex gap-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. youtube, example.com" />
              <Button
                variant="outline"
                onClick={() => {
                  setPage(1);
                  fetchAll();
                }}
                data-testid="usage-search"
              >
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Allowed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-indigo-700">
              {summary ? `${summary.allowed.percent}%` : '-'}
            </div>
            <div className="text-sm text-gray-600">{summary ? formatDuration(summary.allowed.seconds) : ''}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Blocked</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">
              {summary ? `${summary.blocked.percent}%` : '-'}
            </div>
            <div className="text-sm text-gray-600">{summary ? formatDuration(summary.blocked.seconds) : ''}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Unlisted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-800">
              {summary ? `${summary.unlisted.percent}%` : '-'}
            </div>
            <div className="text-sm text-gray-600">{summary ? formatDuration(summary.unlisted.seconds) : ''}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Usage Items</CardTitle>
          <CardDescription>
            Showing {items.length} items for {filters.date}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-gray-600">{t("common.loading")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-4">Staff</th>
                    <th className="py-2 pr-4">App</th>
                    <th className="py-2 pr-4">Domain</th>
                    <th className="py-2 pr-4">Category</th>
                    <th className="py-2 pr-4">Usage</th>
                    <th className="py-2 pr-4">% of day</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-2 pr-4">{row.user || row.staff_id}</td>
                      <td className="py-2 pr-4">{row.app_name || '-'}</td>
                      <td className="py-2 pr-4">{row.normalized_domain || '-'}</td>
                      <td className="py-2 pr-4"><CategoryBadge category={row.category} /></td>
                      <td className="py-2 pr-4">{formatDuration(row.usage_seconds)}</td>
                      <td className="py-2 pr-4">{row.usage_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                >
                  Prev
                </Button>
                <div className="text-sm text-gray-600">Page {page}</div>
                <Button
                  variant="outline"
                  onClick={() => setPage(page + 1)}
                  disabled={items.length < limit}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
